import { useState, useMemo } from 'react';
import { Button, Card, Flex, Modal, Stack, useTheme, spacing } from '@fti/design-system';
import { usePreviewArticles, useArticleDetail } from '../../../api/media-sim/publications';
import type { ArticlePreviewResult } from '../../../types/media-sim';

interface ArticlePreviewProps {
  label: string;
  terms: string[];
  collectionIds: string[];
  helpText?: string;
}

const ARTICLES_PER_PAGE = 15;

function PublicationGroup({
  publisher,
  articles,
  onSelect,
}: {
  publisher: string;
  articles: ArticlePreviewResult[];
  onSelect: (article: ArticlePreviewResult) => void;
}) {
  const { theme } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [page, setPage] = useState(0);

  const pageCount = Math.ceil(articles.length / ARTICLES_PER_PAGE);
  const start = page * ARTICLES_PER_PAGE;
  const visible = articles.slice(start, start + ARTICLES_PER_PAGE);

  return (
    <Card variant="outlined">
      <Card.Header
        title={publisher}
        subtitle={`${articles.length} article(s)`}
        action={
          <button
            onClick={() => setExpanded(!expanded)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: theme.colors.primary[500],
              fontSize: '0.85rem',
              fontWeight: 500,
            }}
          >
            {expanded ? 'Collapse' : 'Expand'}
          </button>
        }
      />
      {expanded && (
        <Card.Body>
          <Stack gap="xs">
            {visible.map((article, i) => (
              <Flex
                key={start + i}
                gap="sm"
                align="start"
                onClick={() => onSelect(article)}
                style={{
                  padding: `${spacing.xs} ${spacing.sm}`,
                  cursor: 'pointer',
                  borderRadius: '4px',
                  borderBottom: i < visible.length - 1 ? `1px solid ${theme.colors.border.default}` : undefined,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = theme.colors.background.paper)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 500, color: theme.colors.primary[700] }}>
                    {article.title}
                  </p>
                  <span style={{ fontSize: '0.75rem', color: theme.colors.text.secondary }}>
                    {article.date}
                  </span>
                </div>
              </Flex>
            ))}
          </Stack>
          {pageCount > 1 && (
            <Flex justify="between" align="center" style={{ marginTop: spacing.sm }}>
              <span style={{ fontSize: '0.75rem', color: theme.colors.text.secondary }}>
                Showing {start + 1}–{Math.min(start + ARTICLES_PER_PAGE, articles.length)} of {articles.length}
              </span>
              <Flex gap="xs" align="center">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Prev
                </Button>
                <span style={{ fontSize: '0.75rem', color: theme.colors.text.secondary }}>
                  {page + 1} / {pageCount}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pageCount - 1}
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                >
                  Next
                </Button>
              </Flex>
            </Flex>
          )}
        </Card.Body>
      )}
    </Card>
  );
}

export function ArticlePreview({ label, terms, collectionIds, helpText }: ArticlePreviewProps) {
  const { theme } = useTheme();
  const { preview, loading, error } = usePreviewArticles();
  const { fetchArticle, loading: articleLoading } = useArticleDetail();
  const [articles, setArticles] = useState<ArticlePreviewResult[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<ArticlePreviewResult | null>(null);
  // Full text is lazy-fetched when an article is opened (#198). Cached by uuid
  // so reopening the same article doesn't refetch.
  const [fullTextByUuid, setFullTextByUuid] = useState<Record<string, string>>({});

  const openArticle = async (article: ArticlePreviewResult) => {
    setSelectedArticle(article);
    const uuid = article.uuid;
    // Prefer inline full_text if present (older responses); else fetch by uuid.
    if (!uuid || article.full_text || fullTextByUuid[uuid]) return;
    try {
      const detail = await fetchArticle(article.publication, uuid);
      if (detail) {
        setFullTextByUuid((prev) => ({ ...prev, [uuid]: detail.full_text }));
      }
    } catch { /* surfaced as "unavailable" in the modal */ }
  };

  const handlePreview = async () => {
    if (terms.length === 0 || collectionIds.length === 0) return;
    // Fetch all matched articles (no artificial page cap) so the displayed list
    // matches the total count. The backend already does the expensive Milvus +
    // content-filtering work over the full set before this limit, and the result
    // is bounded upstream by the per-term 200-paragraph search, so this is safe.
    const result = await preview(terms, collectionIds, 0, 10000);
    if (result) {
      setArticles(result.articles);
      setTotalCount(result.total_count);
      setHasSearched(true);
    }
  };

  const grouped = useMemo(() => {
    const map = new Map<string, ArticlePreviewResult[]>();
    for (const article of articles) {
      const key = article.publisher;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(article);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [articles]);

  return (
    <Card variant="outlined">
      <Card.Header
        title={label}
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={handlePreview}
            disabled={loading || terms.length === 0 || collectionIds.length === 0}
            loading={loading}
          >
            {loading ? 'Searching...' : hasSearched ? 'Refresh' : 'Preview'}
          </Button>
        }
      />
      <Card.Body>
        {helpText && (
          <p style={{ color: theme.colors.text.secondary, margin: `0 0 ${spacing.sm}`, fontSize: '0.8rem', lineHeight: 1.5 }}>
            {helpText}
          </p>
        )}
        {hasSearched && !loading && (
          <p style={{ color: theme.colors.text.primary, margin: `0 0 ${spacing.md}`, fontSize: '1rem', fontWeight: 600 }}>
            {totalCount} article(s) across {grouped.length} publication(s)
          </p>
        )}
        {error && (
          <p style={{ color: theme.colors.semantic.error.default, margin: 0, fontSize: '0.85rem' }}>
            {error.message || 'Failed to search articles.'}
          </p>
        )}

        {!hasSearched && !loading && !helpText && (
          <p style={{ color: theme.colors.text.secondary, margin: 0, fontSize: '0.85rem' }}>
            {terms.length === 0
              ? 'Enter terms above, then click Preview to see matching articles.'
              : collectionIds.length === 0
                ? 'Select publications first (Step 3), then preview articles.'
                : 'Click Preview to search for matching articles.'}
          </p>
        )}

        {hasSearched && articles.length === 0 && !loading && (
          <p style={{ color: theme.colors.text.secondary, margin: 0, fontSize: '0.85rem' }}>
            No articles matched these terms. Try different or broader terms.
          </p>
        )}

        {grouped.length > 0 && (
          <Stack gap="sm">
            {grouped.map(([publisher, pubArticles]) => (
              <PublicationGroup
                key={publisher}
                publisher={publisher}
                articles={pubArticles}
                onSelect={openArticle}
              />
            ))}
          </Stack>
        )}
      </Card.Body>

      {selectedArticle && (
        <Modal isOpen onClose={() => setSelectedArticle(null)} size="lg">
          <Modal.Header>
            <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>{selectedArticle.title}</h2>
            <p style={{ margin: `${spacing.xs} 0 0`, fontSize: '0.8rem', color: theme.colors.text.secondary }}>
              {selectedArticle.publisher} &middot; {selectedArticle.date}
            </p>
          </Modal.Header>
          <Modal.Body>
            <div
              style={{
                whiteSpace: 'pre-wrap',
                fontSize: '0.85rem',
                lineHeight: 1.7,
                color: theme.colors.text.primary,
                maxHeight: '60vh',
                overflowY: 'auto',
              }}
            >
              {(() => {
                const body =
                  selectedArticle.full_text ||
                  (selectedArticle.uuid ? fullTextByUuid[selectedArticle.uuid] : '');
                if (body) return body;
                if (articleLoading) return 'Loading article...';
                return 'No full text available.';
              })()}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline" onClick={() => setSelectedArticle(null)}>Close</Button>
          </Modal.Footer>
        </Modal>
      )}
    </Card>
  );
}
